import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useClerk } from '@clerk/clerk-react';
import { useAuthConfig } from '@/App';
import { api } from '@/lib/api';
import { clerkEnabled } from '@/lib/clerk';

/** Sign out after this long without any user activity in the tab. Mirrors the
 *  server's sliding token expiry so the two never disagree by much. */
export const IDLE_LIMIT_MS = 60 * 60 * 1000; // 1 hour
const CHECK_EVERY_MS = 60 * 1000;

/** Watches pointer/keyboard/scroll activity and fires `onIdle` once when the
 *  idle limit is exceeded. Also checks on tab re-focus, so a laptop waking
 *  from sleep signs out immediately instead of waiting for the next tick. */
export function useIdleSignOut(onIdle: () => void, enabled: boolean) {
  const lastActivity = useRef(Date.now());
  const fired = useRef(false);
  // Keep the latest onIdle in a ref so the effect below depends only on
  // `enabled`. Otherwise Clerk's ~50s token-refresh re-renders (which can
  // change onIdle's identity) would tear down and recreate the check interval
  // before it ever reaches CHECK_EVERY_MS — and idle would never be detected.
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return;
    const mark = () => {
      lastActivity.current = Date.now();
    };
    const check = () => {
      if (!fired.current && Date.now() - lastActivity.current >= IDLE_LIMIT_MS) {
        fired.current = true;
        onIdleRef.current();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    const events = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'];
    for (const e of events) window.addEventListener(e, mark, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    const interval = setInterval(check, CHECK_EVERY_MS);
    return () => {
      for (const e of events) window.removeEventListener(e, mark);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [enabled]);
}

/** Clerk mode: the session lives with Clerk, so idle sign-out goes through it. */
function ClerkIdleLogout() {
  const { signOut } = useClerk();
  const qc = useQueryClient();
  const onIdle = useCallback(() => {
    qc.clear();
    void signOut({ redirectUrl: '/' });
  }, [qc, signOut]);
  useIdleSignOut(onIdle, true);
  return null;
}

/** Local mode: clear the session cookie, then reload so the auth boundary
 *  lands on the login page. Disabled in dev mode (it auto-signs back in). */
function LocalIdleLogout() {
  const { data: config } = useAuthConfig();
  const qc = useQueryClient();
  const onIdle = useCallback(() => {
    void api
      .post('/api/auth/logout')
      .catch(() => undefined)
      .finally(() => {
        qc.clear();
        window.location.reload();
      });
  }, [qc]);
  useIdleSignOut(onIdle, config?.mode === 'local');
  return null;
}

/** Mounted once inside the signed-in app shell. Renders nothing. */
export function IdleLogout() {
  return clerkEnabled ? <ClerkIdleLogout /> : <LocalIdleLogout />;
}
